<script setup lang="ts">
/**
 * @see Figma (regulatory variant) https://www.figma.com/design/PraFr08GG7pzJBgHfgDWZP/AT---OFICIAL?node-id=1964-23004&m=dev
 */
type Props = {
  modelValue: string[] | boolean;
  label: string;
  value: string;
  lightTheme?: boolean;
  variant?: 'default' | 'regulatory';
};

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
});

const emit = defineEmits(['update:modelValue']);

const model = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    emit('update:modelValue', value);
  },
});

const isRegulatory = computed(() => props.variant === 'regulatory');
</script>

<template>
  <div :class="[$style.checkboxWrapper, { [$style.regulatory]: isRegulatory }]">
    <input
      :id="`checkbox-${value}`"
      v-bind="$attrs"
      v-model="model"
      :class="[$style.checkboxInput, 'peer']"
      type="checkbox"
      :value="value"
    />
    <label
      :class="[
        $style.checkboxLabel,
        { [$style.regulatory]: isRegulatory },
        'group',
        !isRegulatory && lightTheme ? 'text-black/80' : 'text-texts',
      ]"
      :for="`checkbox-${value}`"
    >
      <span
        :class="[
          { [$style.regulatory]: isRegulatory },
          isRegulatory
            ? 'bg-[var(--vipcard-border)] group-[.peer:disabled+&]:cursor-not-allowed'
            : lightTheme
              ? 'bg-black/20 group-[.peer:checked+&]:bg-primary group-[.peer:disabled+&]:bg-black/60 group-[.peer:disabled+&]:cursor-not-allowed'
              : 'bg-white/20 group-[.peer:checked+&]:bg-success group-[.peer:disabled+&]:bg-white/10 group-[.peer:disabled+&]:cursor-not-allowed',
        ]"
      >
        <svg
          class="group-[.peer:checked+&]:fill-white"
          viewBox="0 0 16 16"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.207 4.793a1 1 0 010
          1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5
          9.086l4.293-4.293a1 1 0 011.414 0z"
          />
        </svg>
      </span>
      <p
        :class="[
          $style.checkboxParagraph,
          { [$style.regulatory]: isRegulatory },
        ]"
        v-html="label"
      />
    </label>
  </div>
</template>

<style module lang="scss">
.checkboxWrapper {
  @apply flex items-center cursor-pointer;

  &.regulatory {
    @apply w-full min-w-0;
  }
}
.checkboxInput {
  @apply h-0 w-0 invisible;
}
.checkboxLabel {
  @apply flex items-center gap-3 text-sm font-medium cursor-pointer;

  &.regulatory {
    @apply text-sm font-normal w-full min-w-0;
  }

  .checkboxParagraph {
    @apply select-none text-xs leading-5;

    &.regulatory {
      @apply text-[10px] leading-4 flex-1 min-w-0;
    }
  }

  span {
    @apply flex items-center justify-center rounded select-none;

    &.regulatory {
      @apply rounded-md shrink-0 w-4 h-4;

      svg {
        @apply w-4 h-4;
      }
    }

    svg {
      @apply w-6 h-6 fill-white/5;
    }
  }
}
</style>
